import React, { Fragment, useEffect, useState, useRef } from "react";
import Loader from "../../shared/loader/loader";
import {
  AddIcon,
  FilterIcon,
  EditIcon,
  DeleteIcon,
  ResetIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  CSVIcon,
} from "../../../assets/icons/index";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import { ITag } from "../../../model/interface/tag";
import MultiSelectList from "../questions/metadataDropDownList";
import {
  getTags,
  deleteTag,
  addTag,
  updateTag,
} from "../../../api/tag.service";
import Tooltip from "../../shared/common/ellipsisText";
import ManageTag from "./manageTag";
import Constant from "../../../utils/constant";
import DeleteModal from "../../shared/common/deleteModal";
import { IKeyValuePair } from "../../../model/interface/keyValuePair";
import getColors from "../../../model/tagColor";
import { Link, useNavigate } from "react-router";
import ExportToCSV from "../../shared/common/exportToCsv";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { Menu, Transition } from "@headlessui/react";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import MultiStateButton from "../../shared/common/multiStateButton";
import { toast } from "react-toastify";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
const activeMultiState = [
  { label: "Active", value: true },
  { label: "Both", value: null, selected: true },
  { label: "Inactive", value: false },
];

function Tag() {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const navigate = useNavigate();
  const [contentAreas, setContentAreas] = useState<IKeyValuePair[]>([]);
  const [categorys, setCategorys] = useState<IKeyValuePair[]>([]);
  const [selectedContent, setSelectedContent] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [resetDropdownFlag, setResetDropdownFlag] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [tags, setTags] = useState<ITag[]>([]);
  const [tagsBackUp, setTagsBackUp] = useState<ITag[]>([]);
  const [name, setName] = useState<string>("");
  const [tagEdit, setTagEdit] = useState<ITag>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [tagDelete, setTagDelete] = useState<number>(0);
  const [showManagePopup, setShowManagePopup] = useState<boolean>(false);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [orderByColumn, setOrderByColumn] = useState<string>("");
  const [sortDir, setSortDir] = useState<string>("");
  const [popupAction, setPopupAction] = useState<string>(
    Constant.ManageAction.ADD
  );
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const childRef: any = useRef(undefined);
  const [notify, setNotify] = useState<any>({
    visible: false,
    title: "",
    message: "",
    icon: false,
  });
  const [tagId, setTagId] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined);

  function bindTags(isReset?, resetObj?, onFilter?) {
    setLoading(true);
    if (onFilter) setIsFilter(true);

    const tag: ITag = {
      tagId: tagId,
      name: name,
      contentAreaId: selectedContent,
      categoryId: selectedCategory,
      isActive: isActive,
      orderbyColumn: orderByColumn,
      sortDir: onFilter ? "ASC" : sortDir,
      pageSize: onFilter ? defaultPageSize : pageSize,
      pageNumber: onFilter ? defaultPageNumber : pageNumber,
    };
    setLoading(true);
    getTags(isReset ? resetObj : tag).then((response: any) => {
      setTags(response.data.data);
      setLoading(false);
      setTagsBackUp(response.data.data);
      setTotalPages(
        Math.ceil(
          response.data.totalRecords / (isReset ? defaultPageSize : pageSize)
        )
      );
      setTotalRecords(response.data.totalRecords);
      if (onFilter) {
        paginationRef?.current?.onReset();
        pageSizeRef?.current?.onReset();
        setIsFilter(false);
      }
      setLoading(false);
    });
  }

  const resetFilterHandler = () => {
    setLoading(true);
    setResetDropdownFlag(!resetDropdownFlag);
    setSelectedContent("");
    setSelectedCategory("");
    setName("");
    setIsActive(undefined);
    setSortDir("ASC");
    setTagId(0);

    bindTags(true, {
      contentAreaId: "",
      categoryId: "",
      tagId: 0,
      name: "",
      isActive: undefined,
      pageSize: defaultPageSize,
      pageNumber: defaultPageNumber,
    });
    setTimeout(() => {
      setResetDropdownFlag(false);
      setLoading(false);
    }, 1500);
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
  };

  function confirmDelete(idToDelete?: number) {
    if (idToDelete) {
      setShowDeletePopup(true);
      setTagDelete(idToDelete);
    }
  }

  function deleteRecord() {
    setLoading(true);
    deleteTag(tagDelete).then(() => {
      toast.success("Tag deleted successfully");
      setLoading(false);
      setShowDeletePopup(false);
      setTagDelete(0);
      bindTags();
    });
  }

  function closeDeletePopup() {
    setShowDeletePopup(false);
    setTagDelete(0);
  }

  function handleSort(columnName) {
    setOrderByColumn(columnName);
    setSortAscending(!sortAscending);
    setSortDir(sortAscending ? "DESC" : "ASC");
    setLoading(true);
    const tags: ITag = {
      tagId: tagId,
      name: name,
      contentAreaId: selectedContent,
      categoryId: selectedCategory,
      isActive: isActive,
      pageSize: pageSize,
      pageNumber: pageNumber,
      orderbyColumn: columnName,
      sortDir: sortAscending ? "DESC" : "ASC",
    };

    getTags(tags).then((response: any) => {
      setLoading(false);
      setTags(response.data.data);
      setTotalPages(Math.ceil(response.data.totalRecords / pageSize));
    });
  }

  async function bindContentAreas() {
    const service = await import("../../../api/contentArea.service");
    service.getContentArea().then((response) => {
      const contentDataToPass = response.data.data.map((_g) => {
        return {
          label: _g.name,
          value: parseInt(_g.contentAreaId),
        };
      });
      setContentAreas(contentDataToPass);
    });
  }

  async function bindCategorys() {
    const service = await import("../../../api/category.service");
    service.getCategorys().then((response) => {
      const categoryTypes = response.data.data.map((_g) => {
        return {
          label: _g.name,
          value: parseInt(_g.categoryId),
        };
      });
      setCategorys(categoryTypes);
    });
  }

  function getTextColor(colorCode?: string) {
    let textColor = "text-black ";
    const selectedColor = getColors.filter((r) => r.code === colorCode);
    if (selectedColor && selectedColor.length > 0) {
      if (selectedColor[0].isTextWhite) {
        textColor = "text-white ";
      }
    }

    return textColor;
  }

  const handleRedirect = (tag?: ITag) => {
    navigate(
      tag
        ? Constant.EDITTAG.replace(":id", tag.tagId ? tag.tagId.toString() : "")
        : Constant.ADDTAG,
      { state: { objectToEdit: tag } }
    );
  };
  const handleContentRedirect = (contentId: string) => {
    navigate(Constant.EDITCONTENT.replace(":id", contentId));
  };

  useEffect(() => {
    bindContentAreas();
    bindCategorys();
  }, []);

  useEffect(() => {
    if (isLoaded && !isFilter) {
      bindTags();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1 && !isFilter) {
      bindTags();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const exportFn = async () => {
    setLoading(true);
    const tag: ITag = {
      tagId: tagId,
      name: name,
      contentAreaId: selectedContent,
      categoryId: selectedCategory,
      isActive: isActive,
      pageSize: 9999,
      pageNumber: 1,
    };
    setLoading(true);

    const response = getTags(tag);
    const data = (await response).data;
    setLoading(false);

    if (data && data.data && data.data.length > 0) {
      childRef?.current?.onDownload(data.data, "tags.csv");
    } else {
      setNotify({
        visible: true,
        title: "Warning",
        message: "No records to download",
        icon: false,
      });
      setTimeout(() => setNotify({ visible: false }), 1500);
    }
  };

  /** Code to implement input type number to support all browsers */
  useEffect(() => {
    const input = document.getElementById("tagId");
    if (input) {
      input.addEventListener("keypress", (e: any) => {
        const keyValue = e.key;
        if (!e.target.value && e.key == 0) {
          e.preventDefault();
        } else {
          if (!/[0-9.]/.test(keyValue)) {
            e.preventDefault();
          }
        }
      });
    }
  }, []);

  return (
    <AllowedTo perform={Permissions.tag_view} no={() => <AccessDenied />}>
      {loading && <Loader />}
      {showManagePopup && <ManageTag />}

      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}

      <div className="flex-1">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Tag</h3>
              <AllowedTo perform={Permissions.tag_create}>
                <button
                  onClick={() => handleRedirect()}
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                >
                  <AddIcon className="w-5"></AddIcon>
                  Add Tag
                </button>
              </AllowedTo>
            </div>
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Tag Id</label>
                  <div className="flex flex-row items-center contentArea">
                    <input
                      id="tagId"
                      name="tagId"
                      type="text"
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      value={tagId || ""}
                      onChange={(e) => setTagId(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="col-span-3 sm:col-span-3">
                  <label className="text-sm text-gray-700">Name</label>
                  <div className="flex flex-row items-center contentArea">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Content Area</label>
                  <div className="w-full mt-1 text-sm">
                    <MultiSelectList
                      dropValue={contentAreas}
                      defaultValue={""}
                      value={selectedContent}
                      resetDropdown={resetDropdownFlag}
                      dropSelect={setSelectedContent}
                      multiple={false}
                    />
                  </div>
                </div>
                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Category</label>
                  <div className="w-full mt-1 text-sm">
                    <MultiSelectList
                      dropValue={categorys}
                      defaultValue={""}
                      value={selectedCategory}
                      resetDropdown={resetDropdownFlag}
                      dropSelect={setSelectedCategory}
                      multiple={false}
                    />
                  </div>
                </div>
                <div className="col-span-2 flex items-center mt-6">
                  <MultiStateButton
                    labels={activeMultiState}
                    onSelect={(e) => setIsActive(e.value)}
                    onReset={resetDropdownFlag}
                  />
                </div>
              </div>
              <div className="flex justify-start">
                <button
                  type="submit"
                  className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  onClick={() => bindTags(false, false, true)}
                >
                  <FilterIcon className="w-3 h-3"></FilterIcon>
                  <span>Filter</span>
                </button>
                <button
                  type="button"
                  onClick={() => resetFilterHandler()}
                  className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <ResetIcon className="w-3 h-3"></ResetIcon>
                  <span>Reset Filter</span>
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => exportFn()}
                className="mt-4 py-1.5 px-4 inline-flex ml-auto items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
              >
                <CSVIcon className="w-3 h-3 dark:text-white stroke-current"></CSVIcon>
                <span>Export</span>
              </button>
              <ExportToCSV ref={childRef} />
            </div>

            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-5 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider rounded-tl-lg"
                            >
                              <span className="flex">
                                Id
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "tagId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("tagId")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("tagId")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Name
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "name" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("name")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("name")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Content Area
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "contentArea" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("contentArea")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("contentArea")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Category
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "category" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("category")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("category")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white  "
                            >
                              Description
                            </th>

                            <th
                              scope="col"
                              className="w-12 px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {tags.map((tag, index) => (
                            <tr
                              key={tag.tagId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2  pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500 relative">
                                <div
                                  className="flex cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  onClick={(e) => handleRedirect(tag)}
                                >
                                  {tag.isActive ? (
                                    <span className="mr-1 h-4/5 w-0.5 bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                  ) : (
                                    <span className="mr-1 h-4/5 w-0.5 bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                  )}
                                  <Link
                                    className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                    to={Constant.EDITTAG.replace(
                                      ":id",
                                      tag.tagId ? tag.tagId.toString() : ""
                                    )}
                                  >
                                    {tag.tagId}
                                  </Link>
                                </div>
                              </td>
                              <td className="px-3 py-2  whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <p
                                  className={`inline-block bg-[${
                                    tag.color
                                  }] ${getTextColor(
                                    tag.color
                                  )} rounded-full text-xs shadow-sm py-0.5 px-3 `}
                                >
                                  <Link
                                    className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                    to={Constant.EDITTAG.replace(
                                      ":id",
                                      tag.tagId ? tag.tagId.toString() : ""
                                    )}
                                  >
                                    {tag.name}
                                  </Link>
                                </p>
                              </td>
                              <td className="px-3 py-2 flex cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline">
                                <p
                                  onClick={(e) =>
                                    handleContentRedirect(tag.contentAreaId)
                                  }
                                >
                                  <Link
                                    className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                    to={Constant.EDITCONTENT.replace(
                                      ":id",
                                      tag.contentAreaId
                                    )}
                                  >
                                    {tag.contentAreaName}
                                  </Link>
                                </p>
                              </td>
                              <td className="px-3 py-2  whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <p>{tag.categoryName}</p>
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm textleft font-normal text-gray-500">
                                {tag?.description === null ? (
                                  tag.description
                                ) : (
                                  <Tooltip text={tag.description} />
                                )}
                              </td>

                              <td className="flex justify-end items-center px-3 py-2 whitespace-wrap text-sm text-center font-normal text-gray-700">
                                <div className="flex space-x-2 justify-center items-center">
                                  <Menu
                                    as="div"
                                    className="relative flex justify-end items-center"
                                  >
                                    <Menu.Button className="h-7 bg-white inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bg-primary-color">
                                      <span className="sr-only">
                                        Open options
                                      </span>
                                      <DotsVerticalIcon
                                        className="w-5 h-5"
                                        aria-hidden="true"
                                      />
                                    </Menu.Button>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items className="mx-3 origin-top-right absolute right-7 top-0 w-48 mt-1 rounded-md shadow-lg z-10 bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
                                        <AllowedTo
                                          perform={Permissions.tag_modify}
                                        >
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={(e) =>
                                                    handleRedirect(tag)
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm"
                                                  )}
                                                >
                                                  <EditIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300" />
                                                  Edit
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                        </AllowedTo>

                                        <AllowedTo
                                          perform={Permissions.tag_delete}
                                        >
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={() =>
                                                    confirmDelete(tag.tagId)
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm"
                                                  )}
                                                >
                                                  <DeleteIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300" />
                                                  Delete
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                        </AllowedTo>
                                      </Menu.Items>
                                    </Transition>
                                  </Menu>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div
                  className={`flex justify-between items-center ${
                    tags.length === 0 ? "hidden" : ""
                  }`}
                >
                  <Pagination
                    pages={totalPages}
                    setPageNumber={setPageNumber}
                    ref={paginationRef}
                  />
                  <span>
                    <span className="text-sm pr-4 ml-auto text-left-700">
                      Showing {pageNumber} to{" "}
                      {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                      {totalRecords} results
                    </span>

                    <SelectPageSize
                      setPageSize={setPageSize}
                      type={"second"}
                      ref={pageSizeRef}
                    />
                  </span>
                </div>
                {tags.length === 0 && <p className="p-2">No Records Found</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden">
        <span className="bg-[#F3F4F6]"></span>
        <span className="bg-[#E5E7EB]"></span>
        <span className="bg-[#D1D5DB]"></span>
        <span className="bg-[#9CA3AF]"></span>
        <span className="bg-[#6B7280]"></span>
        <span className="bg-[#4B5563]"></span>
        <span className="bg-[#374151]"></span>
        <span className="bg-[#1F2937]"></span>
        <span className="bg-[#111827]"></span>
        <span className="bg-[#FEE2E2]"></span>
        <span className="bg-[#FECACA]"></span>
        <span className="bg-[#FCA5A5]"></span>
        <span className="bg-[#F87171]"></span>
        <span className="bg-[#EF4444]"></span>
        <span className="bg-[#DC2626]"></span>
        <span className="bg-[#B91C1C]"></span>
        <span className="bg-[#991B1B]"></span>
        <span className="bg-[#7F1D1D]"></span>
        <span className="bg-[#FEF3C7]"></span>
        <span className="bg-[#FDE68A]"></span>
        <span className="bg-[#FCD34D]"></span>
        <span className="bg-[#FBBF24]"></span>
        <span className="bg-[#F59E0B]"></span>
        <span className="bg-[#D97706]"></span>
        <span className="bg-[#B45309]"></span>
        <span className="bg-[#92400E]"></span>
        <span className="bg-[#78350F]"></span>
        <span className="bg-[#D1FAE5]"></span>
        <span className="bg-[#A7F3D0]"></span>
        <span className="bg-[#6EE7B7]"></span>
        <span className="bg-[#34D399]"></span>
        <span className="bg-[#10B981]"></span>
        <span className="bg-[#059669]"></span>
        <span className="bg-[#047857]"></span>
        <span className="bg-[#065F46]"></span>
        <span className="bg-[#064E3B]"></span>
        <span className="bg-[#DBEAFE]"></span>
        <span className="bg-[#BFDBFE]"></span>
        <span className="bg-[#93C5FD]"></span>
        <span className="bg-[#60A5FA]"></span>
        <span className="bg-[#3B82F6]"></span>
        <span className="bg-[#2563EB]"></span>
        <span className="bg-[#1D4ED8]"></span>
        <span className="bg-[#1E40AF]"></span>
        <span className="bg-[#1E3A8A]"></span>
        <span className="bg-[#E0E7FF]"></span>
        <span className="bg-[#C7D2FE]"></span>
        <span className="bg-[#A5B4FC]"></span>
        <span className="bg-[#818CF8]"></span>
        <span className="bg-[#6366F1]"></span>
        <span className="bg-[#4F46E5]"></span>
        <span className="bg-[#4338CA]"></span>
        <span className="bg-[#3730A3]"></span>
        <span className="bg-[#312E81]"></span>
        <span className="bg-[#EDE9FE]"></span>
        <span className="bg-[#DDD6FE]"></span>
        <span className="bg-[#C4B5FD]"></span>
        <span className="bg-[#A78BFA]"></span>
        <span className="bg-[#8B5CF6]"></span>
        <span className="bg-[#7C3AED]"></span>
        <span className="bg-[#6D28D9]"></span>
        <span className="bg-[#5B21B6]"></span>
        <span className="bg-[#4C1D95]"></span>
        <span className="bg-[#FCE7F3]"></span>
        <span className="bg-[#FBCFE8]"></span>
        <span className="bg-[#F9A8D4]"></span>
        <span className="bg-[#F472B6]"></span>
        <span className="bg-[#EC4899]"></span>
        <span className="bg-[#DB2777]"></span>
        <span className="bg-[#BE185D]"></span>
        <span className="bg-[#9D174D]"></span>
        <span className="bg-[#831843]"></span>
      </div>
    </AllowedTo>
  );
}

export default Tag;
